import { useState, useMemo, useRef, useEffect } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Cell } from '@/components/Cell';
import { Button } from '@/components/Button';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import { useSupabaseQuery, sb } from '@/hooks/useSupabaseQuery';
import { useEnsureActiveOptionsFresh } from '@/hooks/useEnsureActiveOptionsFresh';
import { fmtDateTime } from '@/lib/format';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import { useTelegram } from '@/providers/TelegramProvider';
import type { EpsilonWatcher } from '@/types/domain';

/**
 * Распарсивает многострочный ввод Epsilon-вотчеров.
 * Формат каждой строки: `SYMBOL bid>=N` или `SYMBOL ask<=N`.
 * Пример:
 *   BTC-20250131-50000-C bid>=0.85
 *   ETH-20250131-2500-P ask<=0.10
 */
export function parseEpsilonBulk(text: string): {
  rows: Array<{ symbol: string; kind: 'bid_gte' | 'ask_lte'; threshold: number }>;
  /** Номера (с 1) непустых строк, которые не удалось разобрать или с порогом ≤ 0. */
  invalidLines: number[];
} {
  const rows: Array<{ symbol: string; kind: 'bid_gte' | 'ask_lte'; threshold: number }> = [];
  const invalidLines: number[] = [];
  text.split('\n').forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) return;
    // Поддерживаем разделители-пробелы/табы и десятичную запятую.
    const m = line.match(/^([A-Z0-9_-]+)\s+(bid>=|ask<=)\s*([0-9]+(?:[.,][0-9]+)?)$/i);
    // Порог 0 недопустим: bid>=0 срабатывает мгновенно (а с авто-исполнением — продаёт позицию).
    const threshold = m ? parsePositiveDecimal(m[3]) : Number.NaN;
    if (!m || !Number.isFinite(threshold)) {
      invalidLines.push(idx + 1);
      return;
    }
    rows.push({
      symbol: m[1].toUpperCase(),
      kind: m[2].toLowerCase() === 'bid>=' ? 'bid_gte' : 'ask_lte',
      threshold,
    });
  });
  return { rows, invalidLines };
}

/** Целое положительное число (auto_max_orders — integer в БД). */
function parsePositiveInt(value: string): number {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return Number.NaN;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) && n > 0 ? n : Number.NaN;
}

function normalizeDecimalInput(value: string): string {
  return value.replace(/,/g, '.');
}

const DECIMAL_INPUT_PATTERN = '[0-9]+([.][0-9]+)?';
const DECIMAL_FIELDS: ReadonlyArray<keyof StagedRow> = ['threshold', 'auto_qty_per_order', 'auto_sell_price', 'repeat_cooldown'];

function parsePositiveDecimal(value: string): number {
  const normalized = normalizeDecimalInput(value).trim();
  if (!/^\d+(\.\d+)?$/.test(normalized)) return Number.NaN;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Number.NaN;
}

type CooldownUnit = 'sec' | 'min';

const REPEAT_COOLDOWN_MIN_SECONDS = 60;
const REPEAT_COOLDOWN_MAX_SECONDS = 86_400;
const DEFAULT_REPEAT_COOLDOWN = '5';
const DEFAULT_REPEAT_UNIT: CooldownUnit = 'min';
/** Сколько опционов группы показываем за раз (группы бывают на тысячи строк). */
const GROUP_PAGE_SIZE = 100;

/** Кулдаун повтора в секундах (целое 60…86400) или NaN. */
function parseCooldownSeconds(value: string, unit: CooldownUnit): number {
  const n = parsePositiveDecimal(value);
  if (!Number.isFinite(n)) return Number.NaN;
  const seconds = Math.round(unit === 'min' ? n * 60 : n);
  return seconds >= REPEAT_COOLDOWN_MIN_SECONDS && seconds <= REPEAT_COOLDOWN_MAX_SECONDS ? seconds : Number.NaN;
}

/** Короткое представление кулдауна: «90 сек», «5 мин», «1 ч 30 мин». */
function fmtCooldown(seconds: number): string {
  if (seconds < 60 || seconds % 60 !== 0) return `${seconds} сек`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes} мин`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

/** Поля повтора для insert: repeat имеет смысл только без авто-исполнения. */
function repeatFields(autoExecute: boolean, enabled: boolean, cooldownSeconds: number): {
  repeat_enabled: boolean;
  repeat_cooldown_seconds: number;
} {
  const on = !autoExecute && enabled;
  return {
    repeat_enabled: on,
    repeat_cooldown_seconds: on && Number.isFinite(cooldownSeconds) ? cooldownSeconds : 300,
  };
}

const COOLDOWN_HINT = 'Кулдаун повтора: от 1 минуты до 24 часов.';

/** Переключатель «Повторять» + ввод кулдауна. Скрыт при авто-исполнении. */
function RepeatControls({
  enabled,
  cooldown,
  unit,
  onEnabled,
  onCooldown,
  onUnit,
  compact = false,
}: {
  enabled: boolean;
  cooldown: string;
  unit: CooldownUnit;
  onEnabled: (v: boolean) => void;
  onCooldown: (v: string) => void;
  onUnit: (v: CooldownUnit) => void;
  compact?: boolean;
}): JSX.Element {
  const invalid = enabled && !Number.isFinite(parseCooldownSeconds(cooldown, unit));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-sm)', color: 'var(--tg-text)' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
        🔁 Повторять
      </label>
      {enabled && (
        <>
          <input
            value={cooldown}
            onChange={(e) => onCooldown(normalizeDecimalInput(e.target.value))}
            inputMode="decimal"
            placeholder="кулдаун"
            title={COOLDOWN_HINT}
            style={{
              flex: compact ? '0 0 70px' : '0 0 90px',
              background: 'var(--tg-secondary-bg)',
              color: 'var(--tg-text)',
              border: invalid ? '1px solid var(--tg-destructive, #E74C3C)' : 'none',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-xs) var(--space-sm)',
              fontSize: 'var(--font-sm)',
              minHeight: 36,
              minWidth: 0,
            }}
          />
          <select
            value={unit}
            onChange={(e) => onUnit(e.target.value === 'sec' ? 'sec' : 'min')}
            style={{
              background: 'var(--tg-secondary-bg)',
              color: 'var(--tg-text)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-xs) var(--space-sm)',
              fontSize: 'var(--font-sm)',
              minHeight: 36,
              cursor: 'pointer',
            }}
          >
            <option value="min">мин</option>
            <option value="sec">сек</option>
          </select>
          {!compact && (
            <span style={{ color: invalid ? 'var(--tg-destructive, #E74C3C)' : 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
              {COOLDOWN_HINT}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Форматирует тип алерта в читаемый вид. */
function fmtKind(kind: 'bid_gte' | 'ask_lte'): string {
  return kind === 'bid_gte' ? 'bid ≥' : 'ask ≤';
}

/** Раздел Epsilon. Пользовательские пороговые алерты bid/ask. */
export function EpsilonPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const { webApp } = useTelegram();
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [bulkAutoExecute, setBulkAutoExecute] = useState(false);
  const [bulkAskQtyPerOrder, setBulkAskQtyPerOrder] = useState('');
  const [bulkAskMaxOrders, setBulkAskMaxOrders] = useState('1');
  const [bulkAutoSellAfterBuy, setBulkAutoSellAfterBuy] = useState(false);
  const [bulkAutoSellPrice, setBulkAutoSellPrice] = useState('');
  const [bulkRepeat, setBulkRepeat] = useState(false);
  const [bulkRepeatCooldown, setBulkRepeatCooldown] = useState(DEFAULT_REPEAT_COOLDOWN);
  const [bulkRepeatUnit, setBulkRepeatUnit] = useState<CooldownUnit>(DEFAULT_REPEAT_UNIT);

  const watchers = useSupabaseQuery<EpsilonWatcher[]>(
    () =>
      sb
        .from('epsilon_watchers')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(500),
    [],
    15_000,
  );

  const history = useSupabaseQuery<EpsilonWatcher[]>(
    () =>
      sb
        .from('epsilon_watchers')
        .select('*')
        .eq('is_active', false)
        .order('triggered_at', { ascending: false })
        .limit(100),
    [],
    60_000,
  );

  const addBulk = async (): Promise<void> => {
    if (!userId) return;
    const { rows: parsed, invalidLines } = parseEpsilonBulk(bulk);
    if (invalidLines.length > 0) {
      // Раньше такие строки молча пропускались, а textarea очищалась — алерты терялись.
      notify('error');
      const message = `Не удалось разобрать строки: ${invalidLines.join(', ')}. Формат: SYMBOL bid>=0.5 или SYMBOL ask<=0.1 (порог > 0).`;
      if (webApp) webApp.showAlert(message);
      else window.alert(message);
      return;
    }
    if (parsed.length === 0) {
      notify('error');
      return;
    }
    const hasAsk = parsed.some((p) => p.kind === 'ask_lte');
    const askQty = parsePositiveDecimal(bulkAskQtyPerOrder);
    const askMaxOrders = parsePositiveInt(bulkAskMaxOrders);
    const autoSellPrice = parsePositiveDecimal(bulkAutoSellPrice);
    if (
      bulkAutoExecute &&
      hasAsk &&
      (!Number.isFinite(askQty) || askQty <= 0 || !Number.isFinite(askMaxOrders) || askMaxOrders <= 0)
    ) {
      notify('error');
      return;
    }
    if (
      bulkAutoExecute &&
      hasAsk &&
      bulkAutoSellAfterBuy &&
      (!Number.isFinite(autoSellPrice) || autoSellPrice <= 0)
    ) {
      notify('error');
      return;
    }
    const repeatCooldown = parseCooldownSeconds(bulkRepeatCooldown, bulkRepeatUnit);
    if (!bulkAutoExecute && bulkRepeat && !Number.isFinite(repeatCooldown)) {
      notify('error');
      const message = COOLDOWN_HINT;
      if (webApp) webApp.showAlert(message);
      else window.alert(message);
      return;
    }
    setBusy(true);
    const rows = parsed.map((p) => ({
      ...p,
      user_id: userId,
      is_active: true,
      auto_execute: bulkAutoExecute,
      auto_qty_per_order: bulkAutoExecute && p.kind === 'ask_lte' ? askQty : null,
      auto_max_orders: bulkAutoExecute && p.kind === 'ask_lte' ? askMaxOrders : null,
      auto_orders_executed: 0,
      auto_order_ttl_seconds: 300,
      auto_sell_after_buy: bulkAutoExecute && p.kind === 'ask_lte' ? bulkAutoSellAfterBuy : false,
      auto_sell_price: bulkAutoExecute && p.kind === 'ask_lte' && bulkAutoSellAfterBuy ? autoSellPrice : null,
      auto_sell_ttl_seconds: null,
      ...repeatFields(bulkAutoExecute, bulkRepeat, repeatCooldown),
    }));
    const { error } = await sb.from('epsilon_watchers').insert(rows);
    setBusy(false);
    if (error) {
      notify('error');
      // eslint-disable-next-line no-console
      console.error(error);
      return;
    }
    notify('success');
    setBulk('');
    watchers.refetch();
  };

  const confirmAction = (message: string): Promise<boolean> =>
    new Promise((resolve) => {
      if (webApp) {
        try {
          webApp.showConfirm(message, (ok) => resolve(Boolean(ok)));
          return;
        } catch {
          // Старые клиенты Telegram (< 6.2) не поддерживают showConfirm — fallback ниже.
        }
      }
      resolve(window.confirm(message));
    });

  const remove = async (w: EpsilonWatcher): Promise<void> => {
    const ok = await confirmAction(`Удалить алерт #${w.symbol} (${fmtKind(w.kind)} ${w.threshold})?`);
    if (!ok) return;
    const { error } = await sb.from('epsilon_watchers').delete().eq('id', w.id);
    if (error) {
      notify('error');
      const message = `Не удалось удалить: ${error.message}`;
      if (webApp) webApp.showAlert(message);
      else window.alert(message);
      return;
    }
    notify('success');
    watchers.refetch();
  };

  return (
    <Page title="Epsilon — мои алерты">
      <Section
        header="Добавить пакетно"
        footer={'Формат строки: SYMBOL bid>=0.85  или  SYMBOL ask<=0.10. Эти же команды работают и в боте.'}
      >
        <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
          <textarea
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder={'BTC-20250131-50000-C bid>=0.85\nETH-20250131-2500-P ask<=0.10'}
            rows={5}
            style={{
              width: '100%',
              background: 'var(--tg-secondary-bg)',
              color: 'var(--tg-text)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 'var(--font-md)',
              resize: 'vertical',
            }}
          />
          <div style={{ marginTop: 'var(--space-md)', display: 'grid', gap: 'var(--space-sm)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', fontSize: 'var(--font-sm)' }}>
              <input
                type="checkbox"
                checked={bulkAutoExecute}
                onChange={(e) => setBulkAutoExecute(e.target.checked)}
              />
              Авто-исполнение (TTL каждой заявки 5 минут)
            </label>
            {!bulkAutoExecute && (
              <RepeatControls
                enabled={bulkRepeat}
                cooldown={bulkRepeatCooldown}
                unit={bulkRepeatUnit}
                onEnabled={setBulkRepeat}
                onCooldown={setBulkRepeatCooldown}
                onUnit={setBulkRepeatUnit}
              />
            )}
            {bulkAutoExecute && (
              <>
                <div style={{ color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                  bid-триггер: продать всю открытую позицию. ask-триггер: серия покупок.
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <input
                    value={bulkAskQtyPerOrder}
                    onChange={(e) => setBulkAskQtyPerOrder(normalizeDecimalInput(e.target.value))}
                    inputMode="decimal"
                    pattern={DECIMAL_INPUT_PATTERN}
                    placeholder="qty_per_order (для ask)"
                    style={{
                      flex: 1,
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                  <input
                    value={bulkAskMaxOrders}
                    onChange={(e) => setBulkAskMaxOrders(e.target.value)}
                    inputMode="numeric"
                    placeholder="max_orders (для ask)"
                    style={{
                      flex: 1,
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', fontSize: 'var(--font-sm)' }}>
                  <input
                    type="checkbox"
                    checked={bulkAutoSellAfterBuy}
                    onChange={(e) => setBulkAutoSellAfterBuy(e.target.checked)}
                  />
                  После исполнения auto-buy выставить auto-sell
                </label>
                {bulkAutoSellAfterBuy && (
                  <input
                    value={bulkAutoSellPrice}
                    onChange={(e) => setBulkAutoSellPrice(normalizeDecimalInput(e.target.value))}
                    inputMode="decimal"
                    pattern={DECIMAL_INPUT_PATTERN}
                    placeholder="Цена auto-sell (напр. 0.2)"
                    style={{
                      width: '100%',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                )}
              </>
            )}
          </div>
          <div style={{ marginTop: 'var(--space-md)' }}>
            <Button onClick={addBulk} fullWidth disabled={busy}>
              {busy ? 'Сохраняю…' : 'Добавить'}
            </Button>
          </div>
        </div>
      </Section>

      {/* Табы: Активные / История */}
      <div
        style={{
          display: 'flex',
          margin: '0 var(--space-md) var(--space-sm)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '0.5px solid var(--tg-separator)',
        }}
      >
        {(['active', 'history'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              minHeight: 40,
              border: 'none',
              background: tab === t ? 'var(--tg-button)' : 'transparent',
              color: tab === t ? 'var(--tg-button-text)' : 'var(--tg-text)',
              fontSize: 'var(--font-md)',
              cursor: 'pointer',
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t === 'active' ? 'Активные' : 'История'}
          </button>
        ))}
      </div>

      {tab === 'active' ? (
        <Section header="Активные триггеры">
          {watchers.isLoading ? (
            <Spinner />
          ) : !watchers.data?.length ? (
            <Empty icon="🎯" title="Нет активных алертов" hint="Добавьте через форму выше или в боте." />
          ) : (
            watchers.data.map((w) => (
                <Cell
                  key={w.id}
                  before={w.kind === 'bid_gte' ? '⬆️' : '⬇️'}
                  title={`#${w.symbol}`}
                  subtitle={[
                    `${fmtKind(w.kind)} ${w.threshold}`,
                    w.auto_execute
                      ? (
                        w.kind === 'ask_lte'
                          ? `auto ${w.auto_orders_executed}/${w.auto_max_orders ?? 0}`
                          : 'auto sell позиции'
                      )
                      : null,
                    !w.auto_execute && w.repeat_enabled
                      ? `🔁 ${fmtCooldown(w.repeat_cooldown_seconds ?? 300)}`
                      : null,
                    !w.auto_execute && w.repeat_enabled && w.triggered_at
                      ? `посл. ${fmtDateTime(w.triggered_at)}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                  after={
                    <Button variant="destructive" onClick={() => void remove(w)}>
                      ✕
                  </Button>
                }
                destructive={false}
              />
            ))
          )}
        </Section>
      ) : (
        <Section header="История алертов">
          {history.isLoading ? (
            <Spinner />
          ) : !history.data?.length ? (
            <Empty icon="📋" title="История пуста" hint="Сработавшие алерты появятся здесь." />
          ) : (
            history.data.map((w) => (
              <Cell
                key={w.id}
                before={w.kind === 'bid_gte' ? '⬆️' : '⬇️'}
                title={`#${w.symbol}`}
                subtitle={[
                  `${fmtKind(w.kind)} ${w.threshold}`,
                  w.triggered_at ? `сработал ${fmtDateTime(w.triggered_at)}` : null,
                  w.triggered_bid != null ? `bid ${w.triggered_bid}` : null,
                  w.triggered_ask != null ? `ask ${w.triggered_ask}` : null,
                  w.auto_execute
                    ? (
                      w.kind === 'ask_lte'
                        ? `auto ${w.auto_orders_executed}/${w.auto_max_orders ?? 0}`
                        : `auto sell · done ${w.auto_orders_executed}`
                    )
                    : null,
                  !w.auto_execute && w.repeat_enabled
                    ? `🔁 ${fmtCooldown(w.repeat_cooldown_seconds ?? 300)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                destructive={false}
              />
            ))
          )}
        </Section>
      )}

      <QuickAdd onAdded={() => watchers.refetch()} />
    </Page>
  );
}

// ─── Строка в таблице «быстрого добавления» ────────────────────────────────
interface StagedRow {
  symbol: string;
  kind: 'bid_gte' | 'ask_lte';
  threshold: string;
  auto_execute: boolean;
  auto_qty_per_order: string;
  auto_max_orders: string;
  auto_sell_after_buy: boolean;
  auto_sell_price: string;
  repeat_enabled: boolean;
  repeat_cooldown: string;
  repeat_unit: CooldownUnit;
}

const BOOLEAN_STAGED_FIELDS: ReadonlyArray<keyof StagedRow> = ['auto_execute', 'auto_sell_after_buy', 'repeat_enabled'];

/** Форматирует дату экспирации из Gate-формата `20250131` в `2025-01-31`. */
function fmtExpiry(raw: string): string {
  if (raw.length !== 8) return raw;
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/** Подкомпонент «Быстрое добавление» с группировкой по криптовалютам. */
function QuickAdd({ onAdded }: { onAdded: () => void }): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const [staged, setStaged] = useState<StagedRow[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Сколько строк показано в каждой раскрытой группе (по умолчанию GROUP_PAGE_SIZE).
  const [groupLimits, setGroupLimits] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [filterSide, setFilterSide] = useState<'all' | 'C' | 'P'>('all');
  const [filterExpiry, setFilterExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Символы, по которым сейчас идёт insert — защита от двойного тапа (ref — синхронно, state — для UI).
  const creatingRef = useRef<Set<string>>(new Set());
  const [creatingSymbols, setCreatingSymbols] = useState<Set<string>>(new Set());

  // Загружаем все активные опционы с авто-обновлением, если данные пустые или устарели.
  const activeOptions = useEnsureActiveOptionsFresh();

  const refreshOptions = async (): Promise<void> => {
    setRefreshing(true);
    try {
      const { error } = await sb.functions.invoke('options-refresher');
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[EpsilonPage] refreshOptions error:', error);
        notify('error');
      } else {
        activeOptions.refetch();
        notify('success');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[EpsilonPage] refreshOptions fetch error:', e);
      notify('error');
    } finally {
      setRefreshing(false);
    }
  };

  // Уникальные даты экспирации из всех опционов (для селектора фильтра).
  const expiryDates = useMemo(() => {
    const now = Date.now();
    const opts = (activeOptions.data ?? []).filter((o) => new Date(o.expiry).getTime() >= now);
    const dates = new Set<string>();
    for (const o of opts) {
      const parts = o.symbol.split('-');
      if (parts.length >= 2) dates.add(parts[1]);
    }
    return [...dates].sort();
  }, [activeOptions.data]);

  // Группируем опционы по базовой криптовалюте (BTC, ETH, SOL, …).
  const groups = useMemo(() => {
    const now = Date.now();
    const opts = (activeOptions.data ?? [])
      .filter((o) => new Date(o.expiry).getTime() >= now)
      .map((o) => o.symbol);
    const q = search.trim().toUpperCase();
    const filtered = opts.filter((sym) => {
      if (q && !sym.includes(q)) return false;
      const parts = sym.split('-');
      if (filterSide !== 'all') {
        const side = parts[parts.length - 1];
        if (side !== filterSide) return false;
      }
      if (filterExpiry) {
        if (parts[1] !== filterExpiry) return false;
      }
      return true;
    });
    const map = new Map<string, string[]>();
    for (const sym of filtered) {
      const prefix = sym.split('-')[0];
      if (!map.has(prefix)) map.set(prefix, []);
      map.get(prefix)!.push(sym);
    }
    return map;
  }, [activeOptions.data, search, filterSide, filterExpiry]);

  // При смене фильтров начинаем снова с первых GROUP_PAGE_SIZE строк.
  useEffect(() => {
    setGroupLimits({});
  }, [search, filterSide, filterExpiry]);

  const showMoreInGroup = (prefix: string): void => {
    setGroupLimits((prev) => ({ ...prev, [prefix]: (prev[prefix] ?? GROUP_PAGE_SIZE) + GROUP_PAGE_SIZE }));
  };

  const toggleGroup = (prefix: string): void => {
    setGroupLimits((prev) => {
      if (!(prefix in prev)) return prev;
      const next = { ...prev };
      delete next[prefix];
      return next;
    });
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  };

  const addToStaged = (symbol: string): void => {
    if (staged.some((r) => r.symbol === symbol)) return;
    setStaged((prev) => [...prev, {
      symbol,
      kind: 'bid_gte',
      threshold: '',
      auto_execute: false,
      auto_qty_per_order: '',
      auto_max_orders: '1',
      auto_sell_after_buy: false,
      auto_sell_price: '',
      repeat_enabled: false,
      repeat_cooldown: DEFAULT_REPEAT_COOLDOWN,
      repeat_unit: DEFAULT_REPEAT_UNIT,
    }]);
  };

  const removeFromStaged = (symbol: string): void => {
    setStaged((prev) => prev.filter((r) => r.symbol !== symbol));
  };

  const updateStaged = (symbol: string, field: keyof StagedRow, value: string | boolean): void => {
    const normalizedValue = typeof value === 'string' && DECIMAL_FIELDS.includes(field)
      ? normalizeDecimalInput(value)
      : value;
    setStaged((prev) =>
      prev.map((r) =>
        (r.symbol === symbol
          ? { ...r, [field]: BOOLEAN_STAGED_FIELDS.includes(field) ? Boolean(normalizedValue) : String(normalizedValue) }
          : r)),
    );
  };

  const createOne = async (row: StagedRow): Promise<void> => {
    const threshold = parsePositiveDecimal(row.threshold);
    if (!userId || !row.threshold || !Number.isFinite(threshold)) {
      notify('error');
      return;
    }
    const askQty = parsePositiveDecimal(row.auto_qty_per_order);
    const askMaxOrders = parsePositiveInt(row.auto_max_orders);
    const autoSellPrice = parsePositiveDecimal(row.auto_sell_price);
    if (
      row.auto_execute &&
      row.kind === 'ask_lte' &&
      (!Number.isFinite(askQty) || askQty <= 0 || !Number.isFinite(askMaxOrders) || askMaxOrders <= 0)
    ) {
      notify('error');
      return;
    }
    if (
      row.auto_execute &&
      row.kind === 'ask_lte' &&
      row.auto_sell_after_buy &&
      (!Number.isFinite(autoSellPrice) || autoSellPrice <= 0)
    ) {
      notify('error');
      return;
    }
    const repeatCooldown = parseCooldownSeconds(row.repeat_cooldown, row.repeat_unit);
    if (!row.auto_execute && row.repeat_enabled && !Number.isFinite(repeatCooldown)) {
      notify('error');
      return;
    }
    if (creatingRef.current.has(row.symbol)) return;
    creatingRef.current.add(row.symbol);
    setCreatingSymbols(new Set(creatingRef.current));
    const { error } = await sb.from('epsilon_watchers').insert({
      user_id: userId,
      symbol: row.symbol,
      kind: row.kind,
      threshold,
      is_active: true,
      auto_execute: row.auto_execute,
      auto_qty_per_order: row.auto_execute && row.kind === 'ask_lte' ? askQty : null,
      auto_max_orders: row.auto_execute && row.kind === 'ask_lte' ? askMaxOrders : null,
      auto_orders_executed: 0,
      auto_order_ttl_seconds: 300,
      auto_sell_after_buy: row.auto_execute && row.kind === 'ask_lte' ? row.auto_sell_after_buy : false,
      auto_sell_price: row.auto_execute && row.kind === 'ask_lte' && row.auto_sell_after_buy ? autoSellPrice : null,
      auto_sell_ttl_seconds: null,
      ...repeatFields(row.auto_execute, row.repeat_enabled, repeatCooldown),
    });
    creatingRef.current.delete(row.symbol);
    setCreatingSymbols(new Set(creatingRef.current));
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    removeFromStaged(row.symbol);
    onAdded();
  };

  const createAll = async (): Promise<void> => {
    if (!userId) return;
    const valid = staged.filter((r) => {
      const threshold = parsePositiveDecimal(r.threshold);
      if (r.threshold === '' || !Number.isFinite(threshold)) return false;
      if (!r.auto_execute && r.repeat_enabled && !Number.isFinite(parseCooldownSeconds(r.repeat_cooldown, r.repeat_unit))) return false;
      if (!r.auto_execute || r.kind !== 'ask_lte') return true;
      const askQty = parsePositiveDecimal(r.auto_qty_per_order);
      const askMaxOrders = parsePositiveInt(r.auto_max_orders);
      const autoSellPrice = parsePositiveDecimal(r.auto_sell_price);
      if (!(Number.isFinite(askQty) && askQty > 0 && Number.isFinite(askMaxOrders) && askMaxOrders > 0)) return false;
      if (r.auto_sell_after_buy) return Number.isFinite(autoSellPrice) && autoSellPrice > 0;
      return true;
    });
    if (valid.length === 0) {
      notify('error');
      return;
    }
    setBusy(true);
    const rows = valid.map((r) => ({
      user_id: userId,
      symbol: r.symbol,
      kind: r.kind,
      threshold: parsePositiveDecimal(r.threshold),
      is_active: true,
      auto_execute: r.auto_execute,
      auto_qty_per_order: r.auto_execute && r.kind === 'ask_lte' ? parsePositiveDecimal(r.auto_qty_per_order) : null,
      auto_max_orders: r.auto_execute && r.kind === 'ask_lte' ? parsePositiveInt(r.auto_max_orders) : null,
      auto_orders_executed: 0,
      auto_order_ttl_seconds: 300,
      auto_sell_after_buy: r.auto_execute && r.kind === 'ask_lte' ? r.auto_sell_after_buy : false,
      auto_sell_price: r.auto_execute && r.kind === 'ask_lte' && r.auto_sell_after_buy ? parsePositiveDecimal(r.auto_sell_price) : null,
      auto_sell_ttl_seconds: null,
      ...repeatFields(r.auto_execute, r.repeat_enabled, parseCooldownSeconds(r.repeat_cooldown, r.repeat_unit)),
    }));
    const { error } = await sb.from('epsilon_watchers').insert(rows);
    setBusy(false);
    if (error) {
      notify('error');
      return;
    }
    notify(valid.length === staged.length ? 'success' : 'warning');
    // Оставляем невалидные строки, чтобы пользователь мог их дозаполнить (раньше они терялись).
    const created = new Set(valid);
    setStaged((prev) => prev.filter((r) => !created.has(r)));
    onAdded();
  };

  const stagedSymbols = new Set(staged.map((r) => r.symbol));

  return (
    <Section header="Быстрое добавление">
      {/* Баннер авто-обновления */}
      {activeOptions.autoRefreshing && (
        <div
          style={{
            padding: 'var(--space-sm) var(--space-lg)',
            background: 'rgba(var(--tg-button-rgb, 51,144,236), 0.08)',
            color: 'var(--tg-hint)',
            fontSize: 'var(--font-sm)',
            borderBottom: '0.5px solid var(--tg-separator)',
          }}
        >
          🔄 Обновляем список опционов с Gate.io…
        </div>
      )}

      {/* Строка поиска + кнопка обновления */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          padding: 'var(--space-sm) var(--space-lg)',
          borderBottom: '0.5px solid var(--tg-separator)',
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск опциона…"
          autoCapitalize="characters"
          style={{
            flex: 1,
            background: 'var(--tg-secondary-bg)',
            border: 'none',
            outline: 'none',
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-md)',
            color: 'var(--tg-text)',
          }}
        />
        <Button variant="secondary" disabled={refreshing} onClick={() => void refreshOptions()} style={{ whiteSpace: 'nowrap' }}>
          {refreshing ? '…' : '🔄'}
        </Button>
      </div>

      {/* Фильтры: тип (Call/Put) + дата экспирации */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          padding: 'var(--space-sm) var(--space-lg)',
          borderBottom: '0.5px solid var(--tg-separator)',
          flexWrap: 'wrap',
        }}
      >
        {/* Кнопки Call / Put / All */}
        {(['all', 'C', 'P'] as const).map((side) => (
          <button
            key={side}
            type="button"
            onClick={() => setFilterSide(side)}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '0.5px solid var(--tg-separator)',
              background: filterSide === side ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
              color: filterSide === side ? 'var(--tg-button-text)' : 'var(--tg-text)',
              fontSize: 'var(--font-sm)',
              cursor: 'pointer',
              fontWeight: filterSide === side ? 600 : 400,
            }}
          >
            {side === 'all' ? 'Все' : side === 'C' ? 'Call' : 'Put'}
          </button>
        ))}

        {/* Селектор даты экспирации */}
        <select
          value={filterExpiry}
          onChange={(e) => setFilterExpiry(e.target.value)}
          style={{
            flex: 1,
            minWidth: 120,
            background: filterExpiry ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
            color: filterExpiry ? 'var(--tg-button-text)' : 'var(--tg-text)',
            border: '0.5px solid var(--tg-separator)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px var(--space-sm)',
            fontSize: 'var(--font-sm)',
            cursor: 'pointer',
          }}
        >
          <option value="">Все даты</option>
          {expiryDates.map((d) => (
            <option key={d} value={d}>{fmtExpiry(d)}</option>
          ))}
        </select>
      </div>

      {/* Список опционов, сгруппированный по базовой криптовалюте */}
      {activeOptions.isLoading ? (
        <Spinner />
      ) : groups.size === 0 ? (
        <div style={{ padding: 'var(--space-lg)', color: 'var(--tg-hint)', fontSize: 'var(--font-sm)', textAlign: 'center' }}>
          {search || filterSide !== 'all' || filterExpiry ? 'Нет совпадений' : 'Список опционов пуст — нажмите 🔄'}
        </div>
      ) : (
        Array.from(groups.entries()).map(([prefix, symbols]) => {
          const isOpen = expandedGroups.has(prefix);
          const limit = groupLimits[prefix] ?? GROUP_PAGE_SIZE;
          const hiddenCount = Math.max(0, symbols.length - limit);
          return (
            <div key={prefix}>
              {/* Заголовок группы */}
              <button
                type="button"
                onClick={() => toggleGroup(prefix)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-md) var(--space-lg)',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '0.5px solid var(--tg-separator)',
                  color: 'var(--tg-text)',
                  fontSize: 'var(--font-md)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 'var(--tap-target)',
                  textAlign: 'left',
                }}
              >
                <span>{prefix}</span>
                <span style={{ color: 'var(--tg-hint)', fontSize: 'var(--font-sm)', fontWeight: 400 }}>
                  {symbols.length} {isOpen ? '▴' : '▾'}
                </span>
              </button>

              {/* Опционы группы */}
              {isOpen &&
                symbols.slice(0, limit).map((sym) => {
                  const isStaged = stagedSymbols.has(sym);
                  return (
                    <div
                      key={sym}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--space-sm) var(--space-lg) var(--space-sm) calc(var(--space-lg) + 8px)',
                        borderBottom: '0.5px solid var(--tg-separator)',
                        background: isStaged ? 'rgba(var(--tg-button-rgb, 51,144,236), 0.08)' : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-sm)',
                          color: isStaged ? 'var(--tg-button)' : 'var(--tg-text)',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sym}
                      </span>
                      <button
                        type="button"
                        onClick={() => (isStaged ? removeFromStaged(sym) : addToStaged(sym))}
                        aria-label={isStaged ? 'Убрать из списка' : 'Добавить в список'}
                        style={{
                          minWidth: 32,
                          minHeight: 32,
                          border: 'none',
                          borderRadius: '50%',
                          background: isStaged ? 'var(--tg-destructive)' : 'var(--tg-button)',
                          color: '#fff',
                          fontSize: 18,
                          lineHeight: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginLeft: 'var(--space-sm)',
                        }}
                      >
                        {isStaged ? '−' : '+'}
                      </button>
                    </div>
                  );
                })}
              {isOpen && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => showMoreInGroup(prefix)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-lg)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '0.5px solid var(--tg-separator)',
                    color: 'var(--tg-link, var(--tg-button))',
                    fontSize: 'var(--font-sm)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-target)',
                  }}
                >
                  {`Показать ещё (${hiddenCount})`}
                </button>
              )}
            </div>
          );
        })
      )}

      {/* Таблица выбранных опционов */}
      {staged.length > 0 && (
        <div style={{ borderTop: '0.5px solid var(--tg-separator)', paddingTop: 'var(--space-sm)' }}>
          <div
            style={{
              padding: '0 var(--space-lg) var(--space-sm)',
              color: 'var(--tg-hint)',
              fontSize: 'var(--font-sm)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Выбрано ({staged.length})
          </div>

          {staged.map((row) => (
            <div
              key={row.symbol}
              style={{
                padding: 'var(--space-sm) var(--space-lg)',
                borderBottom: '0.5px solid var(--tg-separator)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-sm)',
              }}
            >
              {/* Символ */}
              <div
                style={{
                  fontSize: 'var(--font-sm)',
                  fontWeight: 600,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'var(--tg-text)',
                }}
              >
                {row.symbol}
              </div>

              {/* Сторона + Порог + Действие */}
              <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Сторона bid/ask */}
                <select
                  value={row.kind}
                  onChange={(e) => updateStaged(row.symbol, 'kind', e.target.value)}
                  style={{
                    flex: '0 0 auto',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-xs) var(--space-sm)',
                    fontSize: 'var(--font-sm)',
                    cursor: 'pointer',
                    minHeight: 36,
                  }}
                >
                  <option value="bid_gte">bid ≥</option>
                  <option value="ask_lte">ask ≤</option>
                </select>

                {/* Порог */}
                <input
                  value={row.threshold}
                  onChange={(e) => updateStaged(row.symbol, 'threshold', e.target.value)}
                  inputMode="decimal"
                  pattern={DECIMAL_INPUT_PATTERN}
                  placeholder="0.85"
                  style={{
                    flex: 1,
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-xs) var(--space-sm)',
                    fontSize: 'var(--font-sm)',
                    minHeight: 36,
                    minWidth: 0,
                  }}
                  />
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 'var(--font-sm)',
                    color: 'var(--tg-text)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={row.auto_execute}
                    onChange={(e) => updateStaged(row.symbol, 'auto_execute', e.target.checked)}
                  />
                  Авто
                </label>

                {!row.auto_execute && (
                  <RepeatControls
                    compact
                    enabled={row.repeat_enabled}
                    cooldown={row.repeat_cooldown}
                    unit={row.repeat_unit}
                    onEnabled={(v) => updateStaged(row.symbol, 'repeat_enabled', v)}
                    onCooldown={(v) => updateStaged(row.symbol, 'repeat_cooldown', v)}
                    onUnit={(v) => updateStaged(row.symbol, 'repeat_unit', v)}
                  />
                )}

                {row.auto_execute && row.kind === 'ask_lte' && (
                  <>
                    <input
                      value={row.auto_qty_per_order}
                      onChange={(e) => updateStaged(row.symbol, 'auto_qty_per_order', e.target.value)}
                      inputMode="decimal"
                      pattern={DECIMAL_INPUT_PATTERN}
                      placeholder="qty"
                      style={{
                        flex: '0 0 90px',
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        padding: 'var(--space-xs) var(--space-sm)',
                        fontSize: 'var(--font-sm)',
                        minHeight: 36,
                      }}
                    />
                    <input
                      value={row.auto_max_orders}
                      onChange={(e) => updateStaged(row.symbol, 'auto_max_orders', e.target.value)}
                      inputMode="numeric"
                      placeholder="max"
                      style={{
                        flex: '0 0 80px',
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        padding: 'var(--space-xs) var(--space-sm)',
                        fontSize: 'var(--font-sm)',
                        minHeight: 36,
                      }}
                    />
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 'var(--font-sm)',
                        color: 'var(--tg-text)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={row.auto_sell_after_buy}
                        onChange={(e) => updateStaged(row.symbol, 'auto_sell_after_buy', e.target.checked)}
                      />
                      auto-sell
                    </label>
                    {row.auto_sell_after_buy && (
                      <input
                        value={row.auto_sell_price}
                        onChange={(e) => updateStaged(row.symbol, 'auto_sell_price', e.target.value)}
                        inputMode="decimal"
                        pattern={DECIMAL_INPUT_PATTERN}
                        placeholder="sell price (напр. 0.2)"
                        style={{
                          flex: '0 0 96px',
                          background: 'var(--tg-secondary-bg)',
                          color: 'var(--tg-text)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          padding: 'var(--space-xs) var(--space-sm)',
                          fontSize: 'var(--font-sm)',
                          minHeight: 36,
                        }}
                      />
                    )}
                  </>
                )}

                {/* Создать одиночный */}
                <button
                  type="button"
                  onClick={() => void createOne(row)}
                  disabled={creatingSymbols.has(row.symbol)}
                  title="Создать алерт"
                  style={{
                    flex: '0 0 auto',
                    minWidth: 36,
                    minHeight: 36,
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--tg-button)',
                    color: 'var(--tg-button-text)',
                    fontSize: 16,
                    cursor: creatingSymbols.has(row.symbol) ? 'default' : 'pointer',
                    opacity: creatingSymbols.has(row.symbol) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✓
                </button>

                {/* Удалить из списка */}
                <button
                  type="button"
                  onClick={() => removeFromStaged(row.symbol)}
                  title="Убрать"
                  style={{
                    flex: '0 0 auto',
                    minWidth: 36,
                    minHeight: 36,
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'transparent',
                    color: 'var(--tg-destructive)',
                    fontSize: 16,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          {/* Создать всё */}
          <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
            <Button onClick={() => void createAll()} fullWidth disabled={busy}>
              {busy ? 'Сохраняю…' : `Создать всё (${staged.length})`}
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}
